import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { EnfermeraService } from 'src/app/services/enfermera.service';
import { EnfermeraRequest, UsuarioRequest } from 'src/app/models/enfermera-request.interface';

@Component({
  selector: 'app-enfermera-registrar-nuevo',
  templateUrl: './enfermera-registrar-nuevo.component.html',
  styleUrls: ['./enfermera-registrar-nuevo.component.scss'],
  providers: [MessageService]
})
export class EnfermeraRegistrarNuevoComponent {
  enfermeraForm: FormGroup;
  
  // Patrón para contraseña segura (igual que en médico)
  private readonly PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,18}$/;
  
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private messageService: MessageService,
    private enfermeraService: EnfermeraService
  ) { 
    this.enfermeraForm = this.crearFormulario(); 
  }

  onSubmit(): void {
    if (this.enfermeraForm.valid) {
      this.fillEnfermera();
    } else {
      this.showError('Por favor complete todos los campos requeridos correctamente');
      this.markFormGroupTouched(this.enfermeraForm);
    }
  }

  crearFormulario(): FormGroup {
    return this.fb.group({
      usuario: ['', [
        Validators.required, 
        Validators.email,
        Validators.minLength(5),
        Validators.maxLength(50)
      ]],
      password: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(18), // Cambiado a 18 para coincidir con el patrón
        Validators.pattern(this.PASSWORD_PATTERN) // Añadido el patrón de seguridad
      ]],
      alias: ['', [
        Validators.required,
        Validators.maxLength(50)
      ]],
      nombre: ['', [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/)
      ]],
      primerApellido: ['', [
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/)
      ]],
      segundoApellido: ['', [
        Validators.maxLength(100),
        Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/)
      ]],
      telefonoMovil: ['', [
        Validators.required,
        Validators.pattern(/^[0-9]{10}$/)
      ]],
      fk_rol: [5],
      fk_puntos: [1, Validators.required],
      puntos: [500]
    });
  }

  fillEnfermera(): void {
    const { usuario, password, alias, nombre, primerApellido, segundoApellido, telefonoMovil, fk_rol, fk_puntos, puntos } = this.enfermeraForm.value;
    const usuarioRequest: UsuarioRequest = { 
      usuario, 
      password, 
      alias, 
      nombre, 
      primerApellido, 
      segundoApellido, 
      telefonoMovil, 
      fk_rol 
    };
    const correo = usuario;
    const enfermeraRequest: EnfermeraRequest = { 
      correo, 
      telefonoMovil, 
      fk_puntos, 
      puntos, 
      usuario: usuarioRequest 
    };

    this.setEnfermera(enfermeraRequest);
  }

  setEnfermera(enfermera: EnfermeraRequest): void {
    this.enfermeraService.nuevaEnfermera(enfermera)
      .subscribe({
        next: (resp) => {
          console.log(resp.datos);
          this.showSuccess(resp.mensaje);
          this.router.navigate(['/registrar']);
        },
        error: (err: HttpErrorResponse) => { 
          this.showError(err.error?.message || 'Error al registrar la enfermera'); 
          console.log(err);
        }
      });
  }

  cancelar(): void {
    this.router.navigate(['/registrar']);
  }

  showSuccess(mensaje: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Éxito',
      detail: mensaje
    });
  }

  showError(mensaje: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: mensaje
    });
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get f() {
    return this.enfermeraForm.controls;
  }

  // Método para verificar si un campo tiene error específico
  hasError(controlName: string, errorName: string): boolean {
    const control = this.enfermeraForm.get(controlName);
    return !!control && control.touched && control.hasError(errorName);
  }
}